/* payrollKeys.js — Path C Session 3a
 *
 * Single source of truth for payroll IS-line-key generation. Consumed by
 * computePayroll.js (output key emission) and buildISLines.js (dynPayrollLeaves
 * id assignment).
 *
 * Key format: <prefix>_[<display_group>_]<paytype>
 *   - prefix: is_category slug — 'cogs' | 'sm' | 'ga' | 'rd'
 *   - display_group: optional slug from department_mappings.display_group
 *   - paytype: 'wages' | 'bonuses' | 'payroll_taxes' | 'benefits' | 'commissions'
 *
 * Middle-drop rule: when display_group is null OR equals the prefix, the
 * middle segment is omitted. This preserves legacy catch-all keys
 * (cogs_wages, sm_wages, etc.) for tenants without department_mappings and
 * for collapsed-display_group cases on per-line tenants (AE Model COGS/G&A/R&D
 * where the single display_group equals its prefix).
 *
 * Commissions path: this helper emits the key shape unconditionally. The
 * decision of WHICH paytypes to actually emit is the consumer's
 * responsibility — see operator clarification for Session 3:
 *   - Tenants with deptMappings (AE Model): fold commission values into
 *     the bonuses paytype output; do NOT emit a separate commissions key.
 *   - Tenants without deptMappings (Semitek/Chris Test legacy): preserve
 *     existing 5-paytype emission including separate sm_commissions etc.
 *
 * Session 3a: this file exists but is not yet consumed by any engine.
 * Session 3b adds the consumers.
 */

/**
 * Generate the IS line key for a (is_category, display_group, paytype) tuple.
 *
 * @param {string} isCategorySlug - 'cogs' | 'sm' | 'ga' | 'rd'
 * @param {?string} displayGroup  - slug or null
 * @param {string} paytype        - 'wages' | 'bonuses' | 'payroll_taxes' | 'benefits' | 'commissions'
 * @returns {string} The IS line key, e.g. 'sm_client_success_wages' or 'cogs_wages'.
 */
export function buildPayrollIsLineKey(isCategorySlug, displayGroup, paytype) {
  if (displayGroup == null || displayGroup === isCategorySlug) {
    return `${isCategorySlug}_${paytype}`;
  }
  return `${isCategorySlug}_${displayGroup}_${paytype}`;
}

export const PAYTYPES = ['wages', 'bonuses', 'payroll_taxes', 'benefits', 'commissions'];

/* The ONE label owner for a paytype as an operator CHOOSES it (commission ship 2b, 2026-10-09: the Step-3 "pay type" of a
 * "Handled by Payroll" row, stored on gl_line_mappings.payroll_paytype). Keyed by PAYTYPES -- one entry each, no more.
 * (The income-statement line labels -- "Salaries", "Payroll Taxes" -- are a different thing and live in buildISLines.) */
export const PAYTYPE_CHOICE_LABELS = {
  wages:         'Wages',
  bonuses:       'Bonuses',
  commissions:   'Commissions',
  payroll_taxes: 'Payroll taxes',
  benefits:      'Benefits',
};

/* ★★ THE ONE role → paytype MAP.
 *
 * A payroll ROLE (what `matchPayrollRole` returns, and what gl_line_mappings.role
 * stores) is NOT the same token as a PAYTYPE (what composes a key suffix and what
 * PAYTYPE_SORT_OFFSET is keyed on). This is the only place that translation lives.
 *
 * ★ THE TRAP THAT MAKES IT A MAP RATHER THAN STRING SURGERY: the role is
 * `payroll_tax` — SINGULAR, and it KEEPS the `payroll_` prefix — while the paytype is
 * `payroll_taxes`, PLURAL. The other four are simply the role minus `payroll_`, so a
 * `role.replace(/^payroll_/, '')` shortcut is right four times out of five: the worst
 * possible shape, because it looks correct and ships a wrong FROZEN is_line_key for
 * every payroll-tax line.
 *
 * ★ EVERY VALUE MUST BE A MEMBER OF `PAYTYPES` ABOVE. The map sits here, beside the
 * vocabulary it has to agree with, precisely so the two cannot drift into separate
 * files and disagree quietly.
 *
 * ⓘ IT USED TO EXIST TWICE: as this object in AdminPage.js (added by B6, which
 * deleted the page's private payroll matcher) and as an inline ternary chain inside
 * buildIsSortOrders (inferIsMeta.js). They agreed on all five roles — but the ternary
 * reached that agreement through a trailing fall-through that answered "commissions"
 * for ANY unrecognised role, so it was right by accident rather than by rule. Both
 * copies now import this one. */
export const PAYROLL_ROLE_TO_PAYTYPE = {
  payroll_wages:       'wages',
  payroll_bonuses:     'bonuses',
  payroll_commissions: 'commissions',
  payroll_tax:         'payroll_taxes',   // ★ role SINGULAR, paytype PLURAL
  payroll_benefits:    'benefits',
};

/* ── Sort bands (Step-3 pass) ────────────────────────────────────────────────
 * Ordering is TWO-LEVEL but driven by ONE field. `sort_order` fixes leaf order
 * directly; GROUP order is DERIVED — buildISLines' emitSection takes the
 * display_groups in first-appearance order within the already-sorted leaves,
 * i.e. effectively min(sort_order) of each group's leaves. So a banded
 * sort_order scheme controls both levels at once.
 *
 * Layout within one display_group's band:
 *     base + 10/20/25/30/40   payroll leaves (wages/bonuses/commissions/
 *                             payroll_taxes/benefits) — ALWAYS FIRST
 *     base + 50 + n           everything else (vendors, D&A, OI…) in the
 *                             tenant's own GL row order
 *
 * ★ sort_order is DISPLAY-ONLY (see CLAUDE.md "IS Architecture Key"): totals
 * are `sumOf.reduce(...)` over an r2()'d, cents-exact addend set, so permuting
 * the array cannot move a number. That is what licenses reordering at all.
 */
export const PAYTYPE_SORT_OFFSET = {
  wages:         10,
  bonuses:       20,
  commissions:   25,
  payroll_taxes: 30,
  benefits:      40,
};

/* Names sort NATURALLY: "Sales 10" comes AFTER "Sales 9", not between "Sales 1" and
 * "Sales 2". Every roster in this app is a list of operator-typed names ending in a number,
 * so a plain localeCompare reads as a defect on every single one of them.
 *
 * It lives here, beside compareDeptRows, because this file is where a roster's order is
 * decided. It had just been written twice in one commit (the Payroll roster and the
 * Scenarios roster) and the TermModal would have been the third -- which is the lookalike
 * the one-engine rule exists to prevent, so the three call one function instead. */
export function compareNamesNaturally(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
}

/* ONE OWNER FOR THE INCOME STATEMENT'S CATEGORY ORDER -- the sequence its sections render in.
 *
 * It is the order buildISLines walks: emitSection cogs -> sm -> (sw/pf/tme, empty on every
 * live tenant) -> ga -> rd. PayrollPage spelled it inline as CAT_ORDER and now calls this;
 * the Scenarios terminations panel had NO category order at all, which is the defect this
 * extraction exists to close.
 *
 * WHY THE PANEL NEEDED IT: it renders its roster as a FLAT list sorted by deptRankMap -- the
 * GLOBAL department rank by (sort_order, id) -- so a category appeared wherever its
 * lowest-ranked department happened to fall. On four of five tenants that put S&M LAST,
 * because S&M's departments carry sort_order 10..50 while COGS/R&D/G&A all carry 0 and sort
 * by id. The income statement puts S&M second.
 *
 * AN UNKNOWN CATEGORY RANKS LAST rather than first, which is what PayrollPage's own build
 * already did -- it appends any category outside this list after the four. Ranking an unknown
 * at 0 would silently promote it above COGS.
 *
 * THE ARGUMENT IS A SLUG, NOT A DISPLAY NAME, so this file stays dependency-free: the
 * display -> slug map is lineRoles' IS_CATEGORY_DISPLAY_TO_SLUG and callers apply it, the
 * same injection discipline buildDgSortBases already uses for its isCategoryToSlug argument. */
export const IS_CATEGORY_ORDER = ["cogs", "sm", "ga", "rd"];

export function isCategoryRank(slug) {
  const i = IS_CATEGORY_ORDER.indexOf(slug);
  return i < 0 ? 9999 : i;
}

/* ONE OWNER FOR THE DEPARTMENT ORDER -- the rule the INCOME STATEMENT uses.
 *
 * It was an inline comparator duplicated in buildDgSortBases and buildDgLabels below, so
 * there was nothing for another surface to CALL. Extracted verbatim, byte-for-byte the same
 * rule: department_mappings.sort_order (the operator's Step-5c arrows), then the row's id.
 *
 * THE TIEBREAK IS LOAD-BEARING AND IT IS `id`, NOT the name and NOT array order. Where a
 * tenant has not set a sequence the rows all carry sort_order 0, and `id` then reproduces
 * the order the departments were created in -- which is what every current tenant renders.
 * A different tiebreak (alphabetical, say) would make the roster and the income statement
 * disagree on exactly those tenants. The comment on buildDgSortBases records the shipped
 * regression that made sorting the input mandatory; this keeps that property.
 *
 * The Payroll roster and the Scenarios roster both call deptRankMap so that the roster
 * groups departments in the statement's order rather than in a second order of its own. */
export function compareDeptRows(a, b) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0);
}

export function sortDeptRows(deptMappings) {
  return [...(deptMappings || [])].sort(compareDeptRows);
}

/* department_name -> rank, in the income statement's order. A department the tenant has no
 * mapping for gets no entry; callers rank it last rather than dropping the row. */
export function deptRankMap(deptMappings) {
  const out = {};
  sortDeptRows(deptMappings).forEach((dm, i) => {
    if (dm && dm.department_name != null && out[dm.department_name] == null) out[dm.department_name] = i;
  });
  return out;
}

/* Width of one display_group's sort band. A subsection with more than
 * (BAND_WIDTH - 50) non-payroll leaves would bleed into the next group's band
 * and silently re-order the statement — callers fail loud instead. */
export const DG_BAND_WIDTH = 1000;

/* Derive the per-display_group sort bases from the tenant's OWN department mappings —
 * replaces the hardcoded DG_SORT_BASE map, which literally named AE Model's four slugs
 * (client_success:1000 … sales_mktg:4000).
 *
 * GROUP ORDER = MIN(department.sort_order) within the group. Bands are handed out per
 * SUBSECTION, walking the departments in sort_order — so the group whose earliest member
 * sorts first gets the lowest band, and therefore renders first.
 *
 * Returns { [catSlug]: { [displayGroup]: base } }, bases 1000, 2000, 3000, …
 *
 * ★★ ORDER-INDEPENDENT BY CONSTRUCTION — THIS FUNCTION SORTS ITS OWN INPUT.
 *
 * This is not a stylistic choice; it is the fix for a shipped regression. The first
 * version walked `deptMappings` in ARRAY ORDER and numbered each display_group by first
 * appearance. That made the income statement's group order depend on the ORDER BY of
 * whatever SELECT fetched the rows:
 *
 *     the gate fed it  ORDER BY id             → client_success, rev_ops, data_science, sales_mktg
 *     production feeds ORDER BY department_name → client_success, data_science, sales_mktg, rev_ops
 *                                                 (server.js — the dept GET)
 *
 * …so the gate passed and production rendered the wrong order. The DELETED hardcode was a
 * literal map keyed by SLUG and was order-independent for free; de-Semitek'ing it traded a
 * hardcoded constant for an order-dependent derivation, and nothing pinned the order.
 *
 * Sorting the input here means ANY caller order — by id, by name, reversed, shuffled —
 * yields identical bands, hence an identical income statement. The id tiebreak makes even
 * equal sort_orders deterministic, so nothing can flap. An engine that does not care how
 * its rows arrived cannot be broken by an ORDER BY someone edits later.
 * (Asserted by the order-independence gate in verify-step3-glm-capture.js.)
 *
 * Byte-identity: every existing dept row has sort_order=0 (the column's default), so ties
 * break on id — reproducing the previous ORDER-BY-id behaviour exactly until the cid=3
 * data migration sets real values. Semitek/Chris Test have zero department_mappings → this
 * returns {} and is never consulted.
 */
export function buildDgSortBases(deptMappings, isCategoryToSlug) {
  const rows = sortDeptRows(deptMappings);
  const out = {};
  for (const dm of rows) {
    const catKey = isCategoryToSlug[dm.is_category];
    if (!catKey) continue;
    const dg = dm.display_group || catKey;
    if (!out[catKey]) out[catKey] = {};
    if (out[catKey][dg] == null) {
      out[catKey][dg] = (Object.keys(out[catKey]).length + 1) * DG_BAND_WIDTH;
    }
  }
  return out;
}

/* dg → operator-set header label, from department_mappings.display_group_label.
 * NULL/absent → the caller falls back to displayGroupLabel(slug) (lineRoles.js), i.e. the
 * hardcoded DISPLAY_GROUP_LABELS map + titlecase. That keeps all three live tenants
 * byte-identical until an operator actually types a label, and lets the AE-Model-shaped
 * literal retire on its own as tenants set real ones.
 *
 * Ties resolve the same way as the bands (sort_order, then id) so a group whose members
 * disagree about the label — which the UI prevents, but the DB does not — is at least
 * DETERMINISTIC rather than dependent on fetch order. */
export function buildDgLabels(deptMappings) {
  const rows = sortDeptRows(deptMappings);
  const out = {};
  for (const dm of rows) {
    const dg = dm.display_group;
    if (!dg || out[dg] != null) continue;
    if (dm.display_group_label) out[dg] = dm.display_group_label;
  }
  return out;
}

/* The band base for one (catSlug, displayGroup). Falls back to the first band
 * when the tenant has no department_mappings for that category — which is the
 * flat/legacy path, where only relative order within the single band matters. */
export function dgSortBase(bases, catSlug, displayGroup) {
  const forCat = bases?.[catSlug];
  if (!forCat) return DG_BAND_WIDTH;
  const dg = displayGroup || catSlug;
  return forCat[dg] ?? DG_BAND_WIDTH;
}

/* ★★ THE ONE PER-(DEPARTMENT, ROLE) PAYROLL KEY INDEX.
 *
 * Returns, for each of the four IS expense categories:
 *   { wages: [...ids], bonuses: [...], payroll_taxes: [...], benefits: [...],
 *     commissions: [...], total: '<the category total line id>' | null }
 *
 * ★ IT READS `line.id`; IT NEVER COMPOSES OR RE-SPELLS A KEY. That is the whole point.
 *   `buildPayrollIsLineKey` above exists to BUILD a key from (cat, dg, paytype); this
 *   function is its inverse and must not duplicate it. The stored id already carries the
 *   frozen identity — including the singular/plural seam this file documents: the leaf whose
 *   ROLE is `payroll_tax` has the ID `cogs_payroll_taxes`. Reading the id is therefore
 *   STRICTLY SAFER than translating the role, because there is nothing left to spell wrong.
 *   PAYROLL_ROLE_TO_PAYTYPE is still used — for the FIELD NAME, which is exactly the
 *   vocabulary it owns.
 *
 * ★ MULTI-DISPLAY-GROUP IS THE REASON THIS RETURNS ARRAYS. A single-dg category collapses to
 *   one key (`cogs_wages`); a multi-dg one emits one per group (`sm_client_success_wages`,
 *   `sm_data_science_wages`, `sm_rev_ops_wages`, `sm_sales_mktg_wages`). Any consumer that
 *   assumes ONE key per (dept, role) is wrong for every multi-dg tenant — which is precisely
 *   the bug this was extracted to close (the Scenarios OpEx breakdown filed 4.87M of S&M
 *   payroll under "Other / Vendors" and showed Payroll Taxes as '--' on all four tenants).
 *
 * ⓘ A category with no leaf for a role gets an EMPTY ARRAY — a real answer, not a missing one.
 *   `commissions` is empty on every current tenant by design: dept-mapped tenants fold
 *   commissions into bonuses, so no leaf carries role=payroll_commissions.
 * ⓘ The category TOTAL is matched on its ROLE (`cogs_total`), not on an id prefix — its id is
 *   `total_cogs`, which does not start with `cogs_`. null when the tenant has no such line.
 */
export const PAYROLL_INDEX_CATS = ['cogs', 'sm', 'ga', 'rd'];

export function buildPayrollKeyIndex(isLines) {
  const out = {};
  for (const cat of PAYROLL_INDEX_CATS) {
    out[cat] = { wages: [], bonuses: [], payroll_taxes: [], benefits: [], commissions: [], total: null };
  }
  for (const line of (isLines || [])) {
    const id = line && line.id;
    if (!id) continue;
    // the category total, keyed on the ROLE (its id is total_<cat>, not <cat>_...)
    const totalCat = PAYROLL_INDEX_CATS.find(c => line.role === c + '_total');
    if (totalCat) { out[totalCat].total = id; continue; }
    // a payroll leaf: the field name comes from the canonical role -> paytype map
    const field = PAYROLL_ROLE_TO_PAYTYPE[line.role];
    if (!field) continue;
    const cat = PAYROLL_INDEX_CATS.find(c => id.indexOf(c + '_') === 0);
    if (!cat) continue;
    if (out[cat][field].indexOf(id) < 0) out[cat][field].push(id);
  }
  return out;
}
